import fetcher, { FleetGetListParams, FleetPaginated } from '../fetcher';

export type MonitoringDocumentSearchCustomerParam = FleetGetListParams &
	Partial<Record<'frmId' | 'fbdhId' | 'ccaId' | 'ccdhId', number>>;

export const monitoringDocumentSearchCustomer = (
	body?: MonitoringDocumentSearchCustomerParam,
) => {
	return fetcher<
		FleetPaginated<{
			bmppGroupName: string;
			ccaName: null;
			ccdhName: null;
			customerId: string;
			customerName: string;
			fbdhName: null;
			frmName: null;
			processStatus: null;
		}>
	>({
		api: '/dms/search-customer',
		method: 'POST',
		body,
	});
};

export type MonitoringDocumentFile = {
	documentId: number;
	documentNo: string;
	fileId: number;
	fileName: string;
	fileUrl: string;
	downloadCount: number;
	lastDownloadBy: string;
	uploadDate: string;
	modifiedDate: string;
	uploadBy?: string;
	uploadedBy?: string;
};

export type MonitoringDocumentDocument = {
	type: string;
	documentType: string;
	name: string;
	files: MonitoringDocumentFile[];
	refDocumentId?: number;
	uploadBy?: string;
	description?: string;
	allowUpload?: boolean;
	prospectId?: string;
};

export type MonitoringDocumentMetadata = {
	scheme?: string;
	mppNo?: string;
	agreementNo?: string;
	disbursementNo?: string;
	type?: 'po' | 'agreement';
	disbursementId?: number;
	poId?: number;
	mklNo?: string;
	prospectId?: string;
	customerName?: string;
	bmppGroupName?: string;
	[key: string]: unknown; // Allow other properties but force type checking when accessing
};

export type MonitoringDocumentSection = {
	id: string;
	label: string;
	documents: MonitoringDocumentDocument[];
	metadata?: MonitoringDocumentMetadata;
};

export type MonitoringDocumentSubCategory = {
	id: string;
	type: string;
	label: string;
	prospectId?: string;
	sections: MonitoringDocumentSection[];
	metadata?: MonitoringDocumentMetadata;
};

export type MonitoringDocumentCategory = {
	id: string;
	type: string;
	label: string;
	fetchType: string;
	subCategories: MonitoringDocumentSubCategory[];
};

export const monitoringDocumentGetMonitoringCustomerDetail = (
	customerId: string,
) => {
	return fetcher<{
		customerId: string;
		customerName: string;
		bmppGroupName: string;
		ccaName: string;
		ccdhName: string;
		fbdhName: string;
		frmName: string;
		processStatus: string;
		categories: MonitoringDocumentCategory[];
	}>({
		api: '/dms/get-monitoring-customer-detail',
		method: 'POST',
		body: {
			customerId,
		},
	});
};

export type MonitoringDocumentCategoryDetailResponse = {
	financingApprovalDocument: {
		details: Array<{
			mklNo: string;
			prospectId: string;
			customerName: string;
			customerId: string;
			bmppGroupId: string;
			bmppGroupName: string;
			documents: MonitoringDocumentDocument[];
		}>;
	} | null;
	covenantDocument: {
		details: Array<{
			mklNo: string;
			prospectId: string;
			customerName: string;
			customerId: string;
			bmppGroupId: string;
			bmppGroupName: string;
			spfList: Array<{
				mklNo: string;
				prospectId: string;
				customerName: string;
				customerId: string;
				bmppGroupId: string;
				bmppGroupName: string;
				spfNo: string;
				spfId: number;
				spfStatus: string;
				documents: MonitoringDocumentDocument[];
			}>;
		}>;
	} | null;
	financingDisbursementDocument: {
		mklList: Array<{
			mklNo: string;
			prospectId: string;
			customerName: string;
			customerId: string;
			bmppGroupId: string;
			bmppGroupName: string;
			mppList: Array<{
				mklNo: string;
				prospectId: string;
				customerName: string;
				customerId: string;
				bmppGroupId: string;
				bmppGroupName: string;
				mppId: number;
				mppNo: string;
				agreementNo: string;
				documents: MonitoringDocumentDocument[];
				disbursementList: Array<{
					disbursementId: number;
					disbursementNo: string;
					scheme: string;
					documents: MonitoringDocumentDocument[];
					purchaseOrderList: Array<{
						purchaseOrderId: number;
						purchaseOrderNo: string;
						documents: MonitoringDocumentDocument[];
						agreementList: Array<{
							agreementId: string;
							agreementNo: string;
							documents: MonitoringDocumentDocument[];
						}>;
					}>;
				}>;
			}>;
		}>;
	} | null;
};

export const monitoringDocumentGetMonitoringCategoryDetail = (
	customerId: string,
	categoryId: string,
) => {
	return fetcher<MonitoringDocumentCategoryDetailResponse>({
		api: '/dms/get-monitoring-category-detail',
		method: 'POST',
		body: {
			customerId,
			categoryId,
		},
	});
};

export const monitoringDocumentGetSignedUrl = (fileId: number) => {
	return fetcher<{
		signedUrl: string;
	}>({
		api: '/dms/get-signed-url',
		method: 'POST',
		body: {
			fileId,
		},
	});
};

export const monitoringDocumentGetDownloadUrl = (fileId: number) => {
	return fetcher<{
		signedUrl: string;
	}>({
		api: '/dms/download-file',
		method: 'POST',
		body: {
			fileId,
		},
	});
};

export const monitoringDocumentUploadFile = (body: {
	customerId: string;
	prospectId?: string;
	generalId: number | null;
	file: {
		id: string;
		name: string;
		path: string;
	};
	slug: string;
}) => {
	return fetcher({
		api: '/dms/upload-file',
		method: 'POST',
		body,
	});
};
